/* eslint-disable react/forbid-component-props */
import { ComponentProps } from 'react';
import {
    Document,
    Font,
    Image,
    Page,
    Path,
    StyleSheet,
    Svg,
    Text,
    View,
} from '@react-pdf/renderer';
import { InvoiceImageSize } from '../invoiceImages';
import { Invoice } from '../types';
import { getPrestigeStickerModel } from './prestigeSticker';
import montserratMedium from './fonts/Montserrat-Medium.ttf';
import montserratSemiBold from './fonts/Montserrat-SemiBold.ttf';
import montserratBold from './fonts/Montserrat-Bold.ttf';
import ibmPlexMonoMedium from './fonts/IBMPlexMono-Medium.ttf';

Font.register({ family: 'Montserrat', src: montserratMedium });
Font.register({ family: 'Montserrat-SemiBold', src: montserratSemiBold });
Font.register({ family: 'Montserrat-Bold', src: montserratBold });
Font.register({ family: 'IBM Plex Mono', src: ibmPlexMonoMedium });

const CONTENT_WIDTH = 528;
const LOGO_WIDTH = 528;
const LOGO_HEIGHT = 233.25;
const FOOTER_WIDTH = 528;
const FOOTER_HEIGHT = 132.75;
const QR_WIDTH = 147;
const COLUMN_GAP = 24;
const CARD_PADDING_LEFT = 24;
const CARD_PADDING_RIGHT = 21;
const FIELDS_MARGIN_RIGHT = 24;
const FIELDS_WIDTH = CONTENT_WIDTH - CARD_PADDING_LEFT
    - CARD_PADDING_RIGHT - FIELDS_MARGIN_RIGHT - QR_WIDTH;
const LABEL_SIZE = 9;
const DETAIL_SIZE = 15;

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Montserrat',
        color: '#2B2B2B',
        backgroundColor: '#ffffff',
        paddingTop: 30,
        paddingHorizontal: 42,
        paddingBottom: 36,
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    logoWrap: {
        alignItems: 'center',
    },
    wordmark: {
        fontSize: 28,
        fontFamily: 'Montserrat-Bold',
        textAlign: 'center',
    },
    card: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1.5,
        borderColor: '#2B2B2B',
        borderRadius: 15,
        paddingTop: 21,
        paddingBottom: 21,
        paddingLeft: CARD_PADDING_LEFT,
        paddingRight: CARD_PADDING_RIGHT,
    },
    fields: {
        width: FIELDS_WIDTH,
        flexGrow: 0,
        flexShrink: 0,
        marginRight: FIELDS_MARGIN_RIGHT,
    },
    qr: {
        width: QR_WIDTH,
        flexGrow: 0,
        flexShrink: 0,
    },
    nombreBlock: {
        paddingBottom: 13.5,
        marginBottom: 15,
        borderBottomWidth: 1,
        borderBottomColor: '#D4D4D4',
    },
    fieldRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 15,
    },
    fieldHalf: {
        flexGrow: 0,
        flexShrink: 0,
        marginRight: COLUMN_GAP,
    },
    fieldHalfLast: {
        flexGrow: 0,
        flexShrink: 0,
    },
    vinBlock: {
        paddingTop: 13.5,
        borderTopWidth: 1,
        borderTopColor: '#D4D4D4',
    },
    fieldLabel: {
        fontFamily: 'Montserrat-SemiBold',
        fontSize: LABEL_SIZE,
        letterSpacing: 1.62,
        color: '#595959',
        marginBottom: 4.5,
    },
    nombreValue: {
        fontFamily: 'Montserrat-Bold',
        fontSize: 20,
        lineHeight: 1.15,
        color: '#2B2B2B',
    },
    detailValue: {
        fontFamily: 'Montserrat-SemiBold',
        fontSize: DETAIL_SIZE,
        lineHeight: 1.15,
        color: '#2B2B2B',
    },
    vinValue: {
        fontFamily: 'IBM Plex Mono',
        fontSize: 18,
        letterSpacing: 1.6,
        lineHeight: 1.15,
        color: '#2B2B2B',
    },
    contact: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#2B2B2B',
        borderRadius: 15,
        paddingVertical: 15,
        paddingHorizontal: 24,
    },
    contactLabel: {
        fontFamily: 'Montserrat-SemiBold',
        fontSize: 10.5,
        letterSpacing: 2.52,
        color: '#ffffff',
        marginRight: 18,
    },
    contactDivider: {
        width: 0.75,
        height: 21,
        backgroundColor: '#8A8A8A',
        marginRight: 18,
    },
    contactPhone: {
        marginLeft: 9,
        fontFamily: 'Montserrat-Bold',
        fontSize: 19.5,
        lineHeight: 1.1,
        color: '#ffffff',
    },
    footer: {
        width: CONTENT_WIDTH,
        alignItems: 'center',
    },
    watermark: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        justifyContent: 'center',
    },
    watermarkText: {
        fontSize: 86,
        letterSpacing: 10,
        color: 'rgba(232, 80, 91, 0.22)',
        fontFamily: 'Montserrat-Bold',
        transform: 'rotate(-35deg)',
    },
});

const oneLine = (value: string) => value.replace(/ /g, '\u00A0');

const trackedWidth = (text: string, fontSize: number, em: number, tracking = 0) => {
    if (!text) return 0;
    return text.length * fontSize * em + Math.max(0, text.length - 1) * tracking;
};

const autoColumnWidth = (telefono: string) => {
    const phoneColumn = Math.max(
        trackedWidth('TELÉFONO', LABEL_SIZE, 0.71, 1.62),
        trackedWidth(telefono, DETAIL_SIZE, 0.61),
    );
    return Math.max(80, FIELDS_WIDTH - phoneColumn - COLUMN_GAP);
};

const createPdfOwnerPassword = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const containBox = (
    naturalWidth: number,
    naturalHeight: number,
    maxWidth: number,
    maxHeight: number,
) => {
    if (naturalWidth <= 0 || naturalHeight <= 0) return undefined;
    const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
    return {
        width: naturalWidth * scale,
        height: naturalHeight * scale,
    };
};

const logoStyle = (
    imageUrl: string,
    imageSizes: Record<string, InvoiceImageSize>,
): ComponentProps<typeof Image>['style'] => {
    const measured = imageSizes[imageUrl];
    const box = measured
        ? containBox(measured.width, measured.height, LOGO_WIDTH, LOGO_HEIGHT)
        : undefined;
    if (!box) return { width: LOGO_WIDTH, maxHeight: LOGO_HEIGHT };
    return { width: box.width, height: box.height };
};

const footerStyle = (
    imageUrl: string,
    imageSizes: Record<string, InvoiceImageSize>,
): ComponentProps<typeof Image>['style'] => {
    const measured = imageSizes[imageUrl];
    const box = measured
        ? containBox(measured.width, measured.height, FOOTER_WIDTH, FOOTER_HEIGHT)
        : undefined;
    if (!box) return { width: FOOTER_WIDTH, maxHeight: FOOTER_HEIGHT };
    return { width: box.width, height: box.height };
};

const ContactIcon = () => (
    <Svg width={19.5} height={19.5} viewBox="0 0 24 24">
        <Path
            d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"
            stroke="#ffffff"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <Path
            d="M9.5 9.2c.2 1.9 3.4 5.1 5.3 5.3l1.2-1.2-1.7-1-1 .6c-.9-.4-1.8-1.3-2.2-2.2l.6-1-1-1.7Z"
            stroke="#ffffff"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </Svg>
);

const PrestigeStickerDocument = ({
    invoice,
    imageSizes,
    qrImageUrl = '',
    qrPixelSize,
    author = '',
}: {
    invoice: Invoice;
    imageSizes: Record<string, InvoiceImageSize>;
    qrImageUrl?: string;
    qrPixelSize?: { width: number; height: number };
    author?: string;
}) => {
    const model = getPrestigeStickerModel(invoice);
    const autoWidth = autoColumnWidth(model.telefono);
    const ownerPassword = createPdfOwnerPassword();
    const qrHeight = qrPixelSize && qrPixelSize.width > 0
        ? QR_WIDTH * (qrPixelSize.height / qrPixelSize.width)
        : QR_WIDTH;

    return (
        <Document
            title={`Sticker ${invoice.number}`.trim()}
            author={author}
            ownerPassword={ownerPassword}
            permissions={{
                printing: 'highResolution',
                modifying: false,
                copying: false,
                annotating: false,
                fillingForms: false,
                contentAccessibility: true,
                documentAssembly: false,
            }}
        >
            <Page size="LETTER" style={styles.page}>
                {model.isVoid ? (
                    <View style={styles.watermark} fixed>
                        <Text style={styles.watermarkText}>VOID</Text>
                    </View>
                ) : null}
                <View style={styles.logoWrap}>
                    {model.logoImageUrl ? (
                        <Image
                            src={model.logoImageUrl}
                            style={logoStyle(model.logoImageUrl, imageSizes)}
                        />
                    ) : (
                        <Text style={styles.wordmark}>{model.wordmark}</Text>
                    )}
                </View>
                <View style={styles.card}>
                    <View style={styles.fields}>
                        <View style={styles.nombreBlock}>
                            <Text style={styles.fieldLabel}>NOMBRE</Text>
                            <Text style={styles.nombreValue}>{oneLine(model.nombre)}</Text>
                        </View>
                        <View style={styles.fieldRow}>
                            <View style={styles.fieldHalf}>
                                <Text style={styles.fieldLabel}>TELÉFONO</Text>
                                <Text style={styles.detailValue}>{oneLine(model.telefono)}</Text>
                            </View>
                            <View style={[styles.fieldHalfLast, { width: autoWidth }]}>
                                <Text style={styles.fieldLabel}>AUTO</Text>
                                <Text style={[styles.detailValue, { width: autoWidth }]}>
                                    {model.auto}
                                </Text>
                            </View>
                        </View>
                        <View style={styles.vinBlock}>
                            <Text style={styles.fieldLabel}>VIN</Text>
                            <Text style={styles.vinValue}>{oneLine(model.vin)}</Text>
                        </View>
                    </View>
                    {qrImageUrl ? (
                        <View style={[styles.qr, { height: qrHeight }]}>
                            <Image
                                src={qrImageUrl}
                                style={{ width: QR_WIDTH, height: qrHeight }}
                            />
                        </View>
                    ) : null}
                </View>
                {model.contactPhone ? (
                    <View style={styles.contact}>
                        <Text style={styles.contactLabel}>PARA CONTACTARNOS</Text>
                        <View style={styles.contactDivider} />
                        <ContactIcon />
                        <Text style={styles.contactPhone}>{model.contactPhone}</Text>
                    </View>
                ) : null}
                {model.footerImages.length ? (
                    <View style={styles.footer}>
                        {model.footerImages.map((image) => (
                            <Image
                                key={image.id}
                                src={image.imageUrl}
                                style={footerStyle(image.imageUrl, imageSizes)}
                            />
                        ))}
                    </View>
                ) : (
                    <View />
                )}
            </Page>
        </Document>
    );
};

export default PrestigeStickerDocument;
